import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSession } from "../lib/session";
import {
  getCompetition, getSwindle, enableSwindle, listPrizes,
  swindleResults, swindlePot, listSwindleEntries, setSwindleEntry, listMembers,
} from "../lib/api";
import { PageHead, Loading, Notice, Empty } from "../components/ui";
import Back from "../components/Back";

/**
 * The swindle: the side bets that run alongside a competition.
 *
 * Everyone puts the same in at the start; it's paid out across a set of
 * small prizes. Nothing about the prize list is fixed — each one is a row
 * saying which round, which holes, which metric and how much.
 *
 * Winners are worked out from the cards, not entered by hand. Correct a
 * score and the swindle re-works itself along with everything else.
 */

const KINDS = [
  { k: "stableford",  label: "Most Stableford points" },
  { k: "nett",        label: "Fewest nett strokes" },
  { k: "gross",       label: "Fewest strokes" },
  { k: "birdie_rate", label: "Rate per birdie or nett eagle" },
];
const HOLES = [
  { k: "all",    label: "All 18" },
  { k: "front9", label: "Front nine" },
  { k: "back9",  label: "Back nine" },
  { k: "par3",   label: "The par 3s" },
  { k: "par4",   label: "The par 4s" },
  { k: "par5",   label: "The par 5s" },
];

const money = (n, ccy = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy })
    .format(Number(n) || 0);

export default function Swindle() {
  const { id } = useParams();
  const nav = useNavigate();
  const { society, profile, isOrganiser } = useSession();

  const [comp, setComp] = useState(null);
  const [swindle, setSwindle] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [results, setResults] = useState([]);
  const [pot, setPot] = useState(null);
  const [entries, setEntries] = useState([]);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("prizes");
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  /**
   * Nothing is shown until everything has arrived.
   *
   * Setting comp and swindle as they landed let the page render while the
   * pot was still null — so every figure fell back to zero and the
   * unallocated-money warning appeared, then corrected itself. Wrong
   * numbers for half a second are worse than a spinner for half a second,
   * because someone might read them.
   */
  const load = async () => {
    try {
      const [c, s] = await Promise.all([getCompetition(id), getSwindle(id)]);
      if (!s) {
        setComp(c); setSwindle(null); setReady(true);
        return;
      }
      const [p, r, pt, e, m] = await Promise.all([
        listPrizes(id), swindleResults(id), swindlePot(id),
        listSwindleEntries(id), listMembers(society.id),
      ]);
      setComp(c); setSwindle(s);
      setPrizes(p); setResults(r); setPot(pt); setEntries(e); setMembers(m);
      setReady(true);
    } catch (e) { setError(e.message); setReady(true); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  /* winners, grouped under the prize they belong to */
  const byPrize = useMemo(() => {
    const m = {};
    results.forEach((r) => { (m[r.prize_id] ||= []).push(r); });
    return m;
  }, [results]);

  const roundName = (rid) =>
    (comp?.rounds ?? []).find((r) => r.id === rid)?.name ?? "Whole competition";

  /**
   * One long list of fifteen prizes is unreadable. Grouped by round it's
   * four short lists, each of which fits on a screen and matches how a
   * society thinks about them — "what's Saturday worth?".
   *
   * Competition-wide prizes go last: they're settled at the end, so
   * they're the least urgent thing on the page.
   */
  const groups = useMemo(() => {
    const rounds = [...(comp?.rounds ?? [])].sort((a, b) => a.round_number - b.round_number);
    const out = rounds.map((r) => ({
      key: r.id, title: r.name, sub: r.courses?.name, date: r.played_on,
      prizes: prizes.filter((p) => p.round_id === r.id),
    })).filter((g) => g.prizes.length > 0);

    const wide = prizes.filter((p) => !p.round_id);
    if (wide.length) out.push({ key: "all", title: "Across the competition",
                                sub: null, date: null, prizes: wide });
    return out;
  }, [prizes, comp]);

  /** What each group is worth, and whether it's been decided yet. */
  const groupTotal = (g) => g.prizes.reduce((t, p) => {
    if (p.kind === "birdie_rate") {
      return t + (byPrize[p.id] ?? []).reduce((a, w) => a + Number(w.payout || 0), 0);
    }
    return t + Number(p.amount || 0);
  }, 0);

  const groupDecided = (g) =>
    g.prizes.every((p) => (byPrize[p.id] ?? []).some((w) => Number(w.payout) > 0));

  /** Open the first group that isn't settled — the one still being played. */
  const firstUndecided = useMemo(() => {
    const g = groups.find((x) => !groupDecided(x));
    return (g ?? groups[groups.length - 1])?.key;
    // eslint-disable-next-line
  }, [groups, results]);

  /** What this member has won, which is what they opened the page for. */
  const myWinnings = useMemo(() =>
    results.filter((r) => r.profile_id === profile?.id)
           .reduce((t, r) => t + Number(r.payout || 0), 0),
    [results, profile]);

  if (error && !comp) return <Notice kind="error">{error}</Notice>;
  if (!ready) return <Loading />;
  if (!comp) return (
    <>
      <Back to="/competitions">Back</Back>
      <Notice kind="error">That competition isn't available.</Notice>
    </>
  );

  /* ---------- not set up yet ---------- */
  if (!swindle) {
    return (
      <>
        <Back to={`/c/${id}/today`}>{comp?.name ?? "Competition"}</Back>
        <PageHead title="Swindle" sub={comp.name} />
        <Empty title="No swindle on this competition"
               action={isOrganiser && (
                 <button className="btn btn-primary" disabled={busy}
                         onClick={async () => {
                           setBusy(true);
                           try {
                             await enableSwindle(id, 10);
                             nav(`/c/${id}/swindle/settings`);
                           }
                           catch (e) { setError(e.message); setBusy(false); }
                         }}>
                   {busy ? "Setting up…" : "Start a swindle"}
                 </button>)}>
          The side bets that run alongside the golf. Everyone puts the same in,
          and it's paid out across whichever prizes your society plays — you
          pick them, rather than being handed a list to prune.
        </Empty>
        <Notice kind="error">{error}</Notice>
      </>
    );
  }

  const ccy = swindle.currency ?? "GBP";
  const promised = Number(pot?.allocated_fixed ?? 0) + Number(pot?.allocated_rate ?? 0);
  const collected = Number(pot?.collected ?? 0);
  const diff = collected - promised;

  return (
    <>
      <Back to={`/c/${id}/today`}>{comp?.name ?? "Competition"}</Back>
      <PageHead title="Swindle" sub={comp.name}>
        {isOrganiser && (
          <Link className="btn btn-ghost" to={`/c/${id}/swindle/settings`}
                style={{ textDecoration: "none" }}>Settings</Link>
        )}
      </PageHead>

      <Notice kind="error">{error}</Notice>

      {/* the pot */}
      <div className="mine" style={{ marginBottom: "1.1rem" }}>
        <div><span className="eyebrow">Buy-in</span><b>{money(swindle.buy_in, ccy)}</b></div>
        <div><span className="eyebrow">Paid up</span>
          <b>{pot?.paid_up ?? 0}<span className="muted" style={{ fontSize: "1rem" }}>
            /{pot?.entered ?? 0}</span></b></div>
        <div><span className="eyebrow">In the pot</span><b>{money(collected, ccy)}</b></div>
        <div><span className="eyebrow">Promised</span>
          <b style={{ color: diff < 0 ? "var(--red)" : undefined }}>{money(promised, ccy)}</b></div>
      </div>

      {diff !== 0 && (
        <div className={`notice ${diff < 0 ? "notice-error" : "notice-info"}`}
             style={{ marginBottom: "1.1rem" }}>
          {diff < 0
            ? `The prizes promise ${money(-diff, ccy)} more than has been collected. Lower a prize, or chase whoever hasn't paid.`
            : `${money(diff, ccy)} of the pot isn't allocated to a prize yet.`}
        </div>
      )}

      <div className="tabs">
        {[["prizes", "Prizes"], ["players", "Who's in"]].map(([k, l]) => (
          <button key={k} data-on={tab === k} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === "prizes" ? (
        prizes.length === 0 ? (
          <Empty title="No prizes yet"
                 action={isOrganiser && (
                   <Link className="btn btn-primary" to={`/c/${id}/swindle/settings`}
                         style={{ textDecoration: "none" }}>Choose the prizes</Link>)}>
            Until there's at least one, the swindle has nothing to pay out.
          </Empty>
        ) : (
          <>
            {myWinnings > 0 && (
              <div className="won">
                <span>You've won</span>
                <b>{money(myWinnings, ccy)}</b>
              </div>
            )}

            <div className="stack" style={{ gap: ".6rem" }}>
              {groups.map((g) => {
                const shown = open[g.key] ?? g.key === firstUndecided;
                const decided = groupDecided(g);
                return (
                  <section className="group" key={g.key}>
                    <button className="group-head"
                            onClick={() => setOpen((o) => ({ ...o, [g.key]: !shown }))}>
                      <span className={`group-caret${shown ? " open" : ""}`} aria-hidden="true" />
                      <span className="group-title">
                        <strong>{g.title}</strong>
                        <small>
                          {[g.sub, g.date &&
                            new Date(g.date).toLocaleDateString("en-GB",
                              { day: "numeric", month: "short" })].filter(Boolean).join(" · ")}
                        </small>
                      </span>
                      <span className="group-meta">
                        <b>{money(groupTotal(g), ccy)}</b>
                        <small>
                          {g.prizes.length} prize{g.prizes.length === 1 ? "" : "s"}
                          {!decided && " · open"}
                        </small>
                      </span>
                    </button>

                    {shown && (
                      <div className="group-body">
                        {g.prizes.map((p) => {
                          const won = byPrize[p.id] ?? [];
                          const rate = p.kind === "birdie_rate";
                          const taken = rate && won.some((w) => w.pot_taken);
                          const scorers = !rate ? won
                            : taken ? won.filter((w) => w.aces > 0)
                            : won.filter((w) => w.value > 0).sort((a, b) => b.value - a.value);
                          return (
                            <div className="prize" key={p.id}>
                              <div className="prize-head">
                                <span style={{ minWidth: 0 }}>
                                  <strong>{p.label.replace(`${g.title} · `, "")}</strong>
                                  <small className="muted">
                                    {KINDS.find((k) => k.k === p.kind)?.label} ·{" "}
                                    {HOLES.find((h) => h.k === p.holes)?.label}
                                  </small>
                                </span>
                                <span className="prize-amount">
                                  {money(p.amount, ccy)}{rate && <small> each</small>}
                                  {rate && p.ace_takes_pot && (
                                    <small style={{ display: "block", color: "var(--red)",
                                                    fontWeight: 700 }}>
                                      ace takes the pot
                                    </small>
                                  )}
                                </span>
                              </div>

                              {rate && won.some((w) => w.pot_taken) && (
                                <div className="ace-note">
                                  A hole in one — the whole pot goes to{" "}
                                  {won.filter((w) => w.aces > 0).map((w) => w.full_name).join(" and ")}.
                                </div>
                              )}

                              {scorers.length === 0 ? (
                                <div className="prize-none">
                                  {rate ? "Nobody yet" : "No cards in yet"}
                                </div>
                              ) : (
                                <div className="prize-winners">
                                  {scorers.map((w) => (
                                    <div className="prize-row" key={w.profile_id}>
                                      <span className={w.profile_id === profile?.id ? "me" : undefined}>
                                        {w.full_name}
                                        {!rate && w.winners > 1 &&
                                          <small className="muted"> · split {w.winners} ways</small>}
                                      </span>
                                      <span className="muted">
                                        {rate ? w.value
                                          : p.kind === "stableford" ? `${w.value} pts` : w.value}
                                      </span>
                                      <b>{money(w.payout, ccy)}</b>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </>
        )
      ) : (
        <>
          <div className="list" style={{ "--cols": "minmax(0,1fr) 5rem" }}>
            <div className="list-row head">
              <span>Player</span><span style={{ textAlign: "right" }}>Paid</span>
            </div>
            {members.map((m) => {
              const e = entries.find((x) => x.profile_id === m.profiles.id);
              return (
                <div className="list-row" key={m.profiles.id}>
                  <span style={{ minWidth: 0, overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.profiles.full_name}
                  </span>
                  <span style={{ textAlign: "right" }}>
                    {isOrganiser ? (
                      <input type="checkbox" checked={!!e?.paid}
                             onChange={async (ev) => {
                               try {
                                 await setSwindleEntry(id, m.profiles.id, ev.target.checked);
                                 const [pt, en] = await Promise.all([swindlePot(id), listSwindleEntries(id)]);
                                 setPot(pt); setEntries(en);
                               } catch (err) { setError(err.message); }
                             }} />
                    ) : (
                      <span className={`pill ${e?.paid ? "pill-mark" : "pill-quiet"}`}>
                        {e?.paid ? "in" : "—"}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="muted" style={{ fontSize: ".75rem", marginTop: ".6rem", lineHeight: 1.6 }}>
            Ticking someone counts their buy-in towards the pot. It doesn't
            affect whether they can win a prize — the cards decide that.
          </p>
        </>
      )}
    </>
  );
}
